import { ReactNode, useState } from 'react';

import { faMagnifyingGlass, faRadar } from '@fortawesome/pro-duotone-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import {
	Autocomplete,
	Box,
	Chip,
	FormHelperText,
	InputAdornment,
	ListItem,
	OutlinedInput,
	Typography,
} from '@mui/material';

import { matchSorter } from 'match-sorter';

import { Group } from './AutocompleteGroup';
import { LabelInput } from './LabelInput';

export function CodeInput({
	label,
	subLabel,
	name,
	onChange,
	value,
	errors,
	codes,
	disabled,
	minInput = 3,
	notFound,
	renderCode,
}: {
	label: string;
	subLabel?: string;
	name: string;
	onChange: (value: number | null | undefined) => void;
	value: number | null | undefined;
	errors: (string | undefined)[];
	codes: {
		category: string;
		items: { id: number; code: string; name: string }[];
	}[];
	disabled?: boolean;
	minInput?: number;
	notFound?: ReactNode;
	renderCode?: (
		code: string,
		item: { id: number; code: string; name: string },
	) => ReactNode;
}) {
	const options = codes.flatMap((category) =>
		category.items.map((item) => ({
			...item,
			category: category.category,
		})),
	);

	const [currentItem, setCurrentItem] = useState(
		options.find((item) => item.id === value) ?? null,
	);
	const [open, setOpen] = useState(false);
	const [inputValue, setInputValue] = useState(currentItem?.name ?? '');

	return (
		<Autocomplete
			open={open}
			onOpen={() => {
				setCurrentItem(null);
				setOpen(true);
			}}
			onClose={(_, closeReason) => {
				if (!currentItem && closeReason !== 'selectOption') {
					setCurrentItem(options.find((item) => item.id === value) ?? null);
				}
				setOpen(false);
			}}
			disabled={disabled}
			value={currentItem}
			noOptionsText={
				<Box
					sx={{
						display: 'flex',
						flexDirection: 'column',
						alignItems: 'center',
						justifyContent: 'center',
						gap: 1,
						padding: 2,
						width: '100%',
						overflow: 'auto',
						background: `radial-gradient(#f9f9f6 15%, transparent 16%) 0 0,
                                    radial-gradient(#f9f9f6 15%, transparent 16%) 8px 8px,
                                    radial-gradient(rgba(255,255,255,.1) 15%, transparent 20%) 0 1px,
                                    radial-gradient(rgba(255,255,255,.1) 15%, transparent 20%) 8px 9px`,
						backgroundColor: '#f3f3f0',
						backgroundSize: '16px 16px',
						borderRadius: 1,
						border: '1px solid',
						borderColor: 'divider',
					}}
				>
					{inputValue.length < minInput ?
						<>
							<FontAwesomeIcon icon={faMagnifyingGlass} size="2x" />
							<Typography variant="subtitle1">
								Type at least {minInput} characters to search
							</Typography>
						</>
					:	(notFound ?? (
							<>
								<FontAwesomeIcon icon={faRadar} size="2x" />
								<Typography variant="subtitle1">No results found</Typography>
							</>
						))
					}
				</Box>
			}
			inputValue={inputValue}
			onInputChange={(_, newInputValue) => {
				setInputValue(newInputValue);
			}}
			onChange={(_, newValue) => {
				setCurrentItem(newValue);
				onChange(newValue ? newValue.id : null);
			}}
			filterOptions={(options) => {
				if (inputValue.length < minInput) {
					return [];
				}
				const filtered = matchSorter(options, inputValue, {
					keys: ['name', 'code', 'category'],
					threshold: matchSorter.rankings.CONTAINS,
				});
				return filtered;
			}}
			options={options}
			getOptionLabel={(option) => option.name}
			groupBy={(option) => {
				const category = codes.find((cat) =>
					cat.items.some((item) => item.id === option.id),
				);
				return category ? category.category : '';
			}}
			renderGroup={(params) => (
				<Group label={params.group}>{params.children}</Group>
			)}
			renderOption={(
				props: React.HTMLAttributes<HTMLLIElement> & { key: React.Key },
				option,
			) => {
				const { key, ...rest } = props;
				return (
					<ListItem
						key={key}
						{...rest}
						sx={{
							'&.MuiAutocomplete-option': {
								display: 'flex',
								gap: 1,
								paddingLeft: 1,
							},
						}}
					>
						{(renderCode && renderCode(option.code, option)) ?? (
							<Chip size="small" label={option.code} />
						)}
						<Typography variant="body2">{option.name}</Typography>
					</ListItem>
				);
			}}
			renderInput={(params) => (
				<LabelInput label={label} fieldName={name} subLabel={subLabel}>
					<OutlinedInput
						size="small"
						{...params.InputProps}
						inputProps={{
							...params.inputProps,
						}}
						error={!!errors.length}
						startAdornment={
							currentItem ?
								<InputAdornment
									position="start"
									sx={{ marginLeft: 1, marginRight: 0 }}
								>
									{renderCode ?
										renderCode(currentItem.code, currentItem)
									:	<Chip size="small" label={currentItem.code} />}
								</InputAdornment>
							:	<InputAdornment position="start" sx={{ marginLeft: 1 }}>
									<FontAwesomeIcon icon={faMagnifyingGlass} />
								</InputAdornment>
						}
					/>
					{errors.length > 0 && (
						<FormHelperText error>{errors[0]}</FormHelperText>
					)}
				</LabelInput>
			)}
			slotProps={{
				listbox: {
					sx: {
						paddingTop: 0,
					},
				},
				paper: {
					sx: {
						'& .MuiAutocomplete-noOptions': {
							padding: 0,
						},
					},
				},
			}}
		/>
	);
}
